import * as Popover from '@radix-ui/react-popover';
import { ChevronDown, Palette } from 'lucide-react';
import React, { useRef } from 'react';
import { appearancePanelInitialFocusSelector } from '../../constants/appearancePanelConstants';
import { OverflowMenuAppearanceRows } from '../OverflowMenu/OverflowMenuAppearanceRows';
import { Tooltip } from '../Tooltip/Tooltip';
import './AppearancePopover.css';

const PANEL_SHELL_CLASS =
  'appearance-popover-panel font-sans min-w-[240px] max-w-[320px] rounded-xl z-appearance-popover overflow-visible outline-none p-0';

/**
 * Portal header control: Accent + Theme in a Radix Popover, matching overflow menu row layout/CSS hooks.
 */
export const AppearancePopover: React.FC = () => {
  const panelContentRef = useRef<HTMLDivElement | null>(null);

  return (
    <Popover.Root modal={false}>
      <Tooltip text="Appearance" fitContent focusableWrapper={false} className="inline-flex shrink-0">
        <Popover.Trigger asChild>
          <button
            type="button"
            className="nav-header-surface-btn appearance-popover-trigger inline-flex items-center justify-center gap-1.5 px-3"
            aria-label="Appearance"
            aria-controls="appearance-panel"
            aria-haspopup="dialog"
          >
            <Palette className="shrink-0 text-primary" size={22} strokeWidth={2} aria-hidden />
            <ChevronDown
              className="shrink-0 text-gray-600 dark:text-[var(--text-secondary)]"
              size={18}
              strokeWidth={2}
              aria-hidden
            />
          </button>
        </Popover.Trigger>
      </Tooltip>
      <Popover.Portal>
        <Popover.Content
          ref={panelContentRef}
          id="appearance-panel"
          role="dialog"
          aria-modal={false}
          side="bottom"
          align="end"
          sideOffset={8}
          collisionPadding={8}
          className={`${PANEL_SHELL_CLASS} origin-top-right data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:zoom-in-95 data-[state=open]:duration-150`}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            requestAnimationFrame(() => {
              const target = panelContentRef.current?.querySelector<HTMLElement>(
                appearancePanelInitialFocusSelector
              );
              target?.focus();
            });
          }}
        >
          <OverflowMenuAppearanceRows markAppearancePanelInitialFocus />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
